import React, { useEffect, useState } from "react";
import { useMutation, useQuery } from "@apollo/client";
import { FaPlusCircle } from "react-icons/fa";
import { Get_Time_Distribution_For_Role, Add_Update_Time_Distribution } from "../../../../../services/userAPI";
import { IoClose } from "react-icons/io5";
import { message, Modal } from "antd";
import { AiOutlineEdit, AiOutlineDelete } from "react-icons/ai";
import { useLocation } from "react-router-dom";
import "./dailyNormConfiguration.css";

const DailyNormConfiguration = ({ signInRequired }) => {
    const location = useLocation();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [dailyActivity, setDailyActivity] = useState("");
    const [dailyNorms, setDailyNorms] = useState("");
    const [editingId, setEditingId] = useState(null);
    const [formSubmitted, setFormSubmitted] = useState(false);
    const [localNorms, setLocalNorms] = useState([]);
    const [initialTotalNorm, setInitialTotalNorm] = useState(null);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [deletingId, setDeletingId] = useState(null);
    const [submitLoading, setSubmitLoading] = useState(false);

    const accountId = location.state?.accountId || null;
    const roleId = location.state?.roleId || null;

    const { data: timeDistData, refetch } = useQuery(Get_Time_Distribution_For_Role, {
        variables: { accountId, roleId },
        skip: !accountId || !roleId,
    });

    const formatSecondsToHHMM = (seconds) => {
        const hours = Math.floor(seconds / 3600).toString().padStart(2, "0");
        const minutes = Math.floor((seconds % 3600) / 60).toString().padStart(2, "0");
        return `${hours}:${minutes} hrs`;
    };

    const formatSecondsToHHMMOnly = (seconds) => {
        const hours = Math.floor(seconds / 3600);
        const minutes = Math.floor((seconds % 3600) / 60);
        return `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}`;
    };

    const hhmmToSeconds = (hhmm) => {
        const [h, m] = hhmm.split(":").map(Number);
        return h * 3600 + m * 60;
    };

    const [addUpdateTimeDistribution, { loading: saveLoading }] = useMutation(Add_Update_Time_Distribution);

    useEffect(() => {
        if (signInRequired) return;

        setLocalNorms([]);
        setInitialTotalNorm(null);
        setIsSubmitted(false);
        setIsModalOpen(false);

        const savedNorms = timeDistData?.getTimeDistributionForRole || [];
        if (savedNorms.length === 0) return;

        const deleteSavedNorms = async () => {
            try {
                const response = await addUpdateTimeDistribution({
                    variables: {
                        data: savedNorms.map((item) => ({
                            id: item.id,
                            name: item.projectName,
                            normInSeconds: item.projectNorm,
                            accountId,
                            roleId,
                            status: 0,
                            depends: false,
                        })),
                    },
                });

                if (response?.data?.addUpdateTimeDistribution?.success) {
                    await refetch();
                } else {
                    message.error("Failed to remove daily norms");
                }
            } catch (error) {
                console.error("Error removing daily norms:", error);
                message.error("Failed to remove daily norms");
            }
        };

        deleteSavedNorms();
    }, [signInRequired, timeDistData, accountId, roleId, refetch, addUpdateTimeDistribution]);

    const calculateTotals = (norms) => {
        if (norms.length === 0) return null;

        const baseNorm = norms.find((norm) => Array.isArray(norm.depends));
        if (!baseNorm) return null;

        const totalOtherNorms = norms
            .filter((item) => item.id !== baseNorm.id)
            .reduce((sum, item) => sum + item.projectNorm, 0);

        return {
            baseNorm,
            baseNormValue: baseNorm.projectNorm,
            totalOtherNorms,
            isEqual: baseNorm.projectNorm === totalOtherNorms,
            difference: Math.abs(baseNorm.projectNorm - totalOtherNorms),
        };
    };

    useEffect(() => {
        if (signInRequired && timeDistData?.getTimeDistributionForRole && localNorms.length === 0) {
            setLocalNorms([...timeDistData.getTimeDistributionForRole]);
            const baseNorm = timeDistData.getTimeDistributionForRole.find((norm) => Array.isArray(norm.depends));
            if (baseNorm) setInitialTotalNorm(baseNorm.projectNorm);
        }
    }, [timeDistData, signInRequired]);

    const totals = calculateTotals(localNorms);
    const isCalcValid = localNorms.length > 1 && totals && totals.isEqual;
    const isSubmitEnabled = isCalcValid && !isSubmitted;

    const handleSave = () => {
        setFormSubmitted(true);
        if (!dailyActivity.trim() || !dailyNorms.trim()) {
            return; // Existing empty field check
        }

        // New validation for exactly four characters (hh:mm format)
        if (dailyNorms.length !== 5 || !dailyNorms.match(/^(\d{2}):(\d{2})$/)) { // 5 characters including colon
            return; // Prevent save and rely on error message to show
        }

        const normInSeconds = hhmmToSeconds(dailyNorms);
        let updatedNorms = [...localNorms];

        if (editingId !== null) {
            const editingItem = updatedNorms.find((item) => item.id === editingId);
            const isEditingBaseNorm = editingItem && Array.isArray(editingItem.depends);

            updatedNorms = updatedNorms.map((item) =>
                item.id === editingId
                    ? {
                        ...item,
                        projectName: dailyActivity,
                        projectNorm: normInSeconds,
                        depends: item.depends,
                    }
                    : item
            );

            if (isEditingBaseNorm) setInitialTotalNorm(normInSeconds);
        } else {
            const isFirstItem = updatedNorms.length === 0;
            updatedNorms.push({
                id: Date.now(),
                projectName: dailyActivity,
                projectNorm: normInSeconds,
                depends: isFirstItem ? [] : null,
                __typename: "ProjectTimeDetails",
            });

            if (isFirstItem) setInitialTotalNorm(normInSeconds);
        }

        // Update local state only, no API call
        setLocalNorms(updatedNorms);
        setIsModalOpen(false);
        setDailyActivity("");
        setDailyNorms("");
        setEditingId(null);
    };

    const sortedNorms = [...localNorms].sort((a, b) =>
        Array.isArray(a.depends) && !Array.isArray(b.depends) ? -1 : Array.isArray(b.depends) && !Array.isArray(a.depends) ? 1 : 0
    );

    const calculateTotalExcludingFirst = (norms) => {
        return norms.slice(1).reduce((acc, curr) => acc + curr.projectNorm, 0);
    };

    const showBaseNormPopup = () => {
        Modal.info({
            // title: "Information",
            content: "Please delete the activities below before proceeding with this deletion.",
            okText: "OK",
            okButtonProps: {
                style: {
                    backgroundColor: "#AD0101", // 👈 maroon
                    borderColor: "#AD0101",
                    // color: "#fff"
                }
            }
        });
    };


    const handleDelete = async (id) => {
        const allNorms =
            localNorms.length > 0
                ? localNorms
                : timeDistData?.getTimeDistributionForRole || [];

        const itemToDelete = allNorms.find((item) => item.id === id);

        if (!itemToDelete) {
            message.error("Item not found");
            return;
        }

        const isBaseNorm = Array.isArray(itemToDelete.depends);
        const hasMultipleActivities = allNorms.length > 1;

        // ✅ SHOW ONLY POPUP (NO DELETE)
        if (isBaseNorm && hasMultipleActivities) {
            showBaseNormPopup();
            return; // 🚨 STOP DELETE
        }

        try {
            setDeletingId(id);

            const payload = [
                {
                    id: itemToDelete.id,
                    name: itemToDelete.projectName,
                    normInSeconds: itemToDelete.projectNorm,
                    accountId,
                    roleId,
                    status: 0,
                    depends: false,
                },
            ];

            const response = await addUpdateTimeDistribution({
                variables: { data: payload },
            });

            if (response?.data?.addUpdateTimeDistribution?.success) {
                message.success("Item deleted successfully!");
                setLocalNorms([]);
                setInitialTotalNorm(null);
                await refetch();
            } else {
                message.error("Failed to delete");
            }
        } catch (error) {
            message.error("Something went wrong");
        } finally {
            setDeletingId(null);
        }
    };

    const handleAddNew = () => {
        if (!signInRequired) return;

        setDailyActivity("");
        setDailyNorms("");
        setEditingId(null);
        setIsModalOpen(true);
        setIsSubmitted(false);
        setFormSubmitted(false);
    };

    const handleEdit = (item) => {
        if (!signInRequired) return;

        if (localNorms.length === 0 && timeDistData?.getTimeDistributionForRole) {
            setLocalNorms([...timeDistData.getTimeDistributionForRole]);
            if (initialTotalNorm === null) {
                const baseNorm = timeDistData.getTimeDistributionForRole.find(
                    (norm) => norm.depends === true || (norm.depends && norm.depends.length)
                );
                if (baseNorm) setInitialTotalNorm(baseNorm.projectNorm);
            }
        }

        setIsSubmitted(false);
        setDailyActivity(item.projectName || "");
        setDailyNorms(formatSecondsToHHMMOnly(item.projectNorm || 0));
        setEditingId(item.id);
        setIsModalOpen(true);
        setFormSubmitted(false);
    };

    const handleNormChange = (e) => {
        let value = e.target.value; // Keep the original value including the colon
        let numericValue = value.replace(/[^0-9]/g, ""); // Extract only numeric parts

        if (numericValue.length > 4) numericValue = numericValue.slice(0, 4); // Limit to 4 digits

        // Handle input with colon
        if (value.includes(":")) {
            const [hours, minutes] = value.split(":");
            let newHours = hours.replace(/[^0-9]/g, "").slice(0, 2) || "";
            let newMinutes = minutes.replace(/[^0-9]/g, "").slice(0, 2) || "";

            // Only pad minutes if we have 4 digits total
            if (numericValue.length === 4 && newMinutes.length > 0) {
                newMinutes = newMinutes.padStart(2, "0");
            }

            // Reconstruct value, keeping it empty if both are empty
            if (newHours.length === 0 && newMinutes.length === 0) {
                value = "";
            } else if (newMinutes.length === 0 && newHours.length > 0) {
                value = newHours; // Remove colon and minutes when backspacing minutes
            } else {
                value = `${newHours}:${newMinutes}`;
            }
        } else if (numericValue.length === 2) {
            // Insert colon after exactly 2 digits
            value = `${numericValue}:`;
        } else if (numericValue.length > 2) {
            // Split into hours and minutes for values > 2 digits
            const hours = numericValue.slice(0, 2);
            const minutes = numericValue.slice(2, 4);
            value = `${hours}:${minutes}`;
        } else {
            // For single digit or empty, use numeric value as is
            value = numericValue;
        }

        setDailyNorms(value);
        if (formSubmitted) setFormSubmitted(false);
    };

    const isValidTimeFormat = (time) => {
        if (!time.trim()) return false; // Empty check
        const [hours, minutes] = time.split(":");
        if (!hours || !minutes || hours.length !== 2 || minutes.length !== 2) return false;
        const h = parseInt(hours, 10);
        const m = parseInt(minutes, 10);
        return h >= 0 && h <= 23 && m >= 0 && m <= 59; // Basic time validation
    };


    const handleSubmit = async () => {
        try {
            if (localNorms.length === 0) {
                message.error("No changes to submit.");
                return;
            }

            const payload = localNorms.map((item) => ({
                id: item.id && item.id.toString().length < 10 ? item.id : null,
                name: item.projectName,
                normInSeconds: item.projectNorm,
                accountId,
                roleId,
                status: 1,
                depends: Array.isArray(item.depends),
            }));

            const response = await addUpdateTimeDistribution({ variables: { data: payload } });
            if (response?.data?.addUpdateTimeDistribution?.success) {
                message.success("Daily Norms submitted successfully!");
                setLocalNorms([]);
                setInitialTotalNorm(null);
                setIsSubmitted(true);
                await refetch();
            } else {
                message.error(response?.data?.addUpdateTimeDistribution?.message || "Submission failed.");
            }
        } catch (error) {
            console.error("Submit error:", error);
            message.error("Something went wrong.");
        }
    };

    const isItemSavedToBackend = (item) => {
        return item.id && typeof item.id === "number" && item.id.toString().length < 10;
    };

    const handleRemoveLocal = (id) => {
        const itemToDelete = localNorms.find((item) => item.id === id);
        if (!itemToDelete) return;

        const isBaseNorm = Array.isArray(itemToDelete.depends);
        const hasMultipleActivities = localNorms.length > 1;

        // ✅ SHOW ONLY POPUP (NO DELETE)
        if (isBaseNorm && hasMultipleActivities) {
            showBaseNormPopup();
            return; // 🚨 STOP DELETE
        }

        const updatedNorms = localNorms.filter((item) => item.id !== id);
        setLocalNorms(updatedNorms);

        if (updatedNorms.length > 0) {
            const newBaseNorm = updatedNorms.find((norm) =>
                Array.isArray(norm.depends)
            );
            if (newBaseNorm) setInitialTotalNorm(newBaseNorm.projectNorm);
        } else {
            setInitialTotalNorm(null);
        }
    };

    return (
        <div className="dailyNorm-container">
            <div className="dailyNorm-card">
                <div className="dailyNorm-header">
                    <span className="daily-config-label">Time Distribution Configuration</span>
                    <button
                        className={`daily-add-signin-btn ${!signInRequired ? "disabled-btn" : ""}`}
                        onClick={handleAddNew}
                        disabled={!signInRequired}
                    >
                        Add Daily Norm
                    </button>
                </div>
                <div className="dailyNorm-table-header">
                    <span className="dailyNorm-header-col">Daily Activity</span>
                    <span className="dailyNorm-header-col">Daily Norms</span>
                    <span className="dailyNorm-header-col" style={{ flex: "none", width: "150px" }}></span>
                </div>
                {sortedNorms.map((item) => {
                    const isBackendSaved = isItemSavedToBackend(item);
                    const isBaseNorm = Array.isArray(item.depends);
                    return (
                        <div key={item.id} className={`dailyNorm-table-row ${isBaseNorm ? "base-norm-row" : ""}`}>
                            <span className="dailyNorm-col">
                                {isBaseNorm && <span className="base-norm-badge">Base</span>}
                                {item.projectName}
                            </span>
                            <span className="dailyNorm-col">{formatSecondsToHHMM(item.projectNorm)}</span>
                            <div className="attendance-actions">
                                <button className="edit-btn" onClick={() => handleEdit(item)}>
                                    Edit
                                </button>
                                {isBackendSaved ? (
                                    <button
                                        className="delete-btn"
                                        onClick={() => handleDelete(item.id)}
                                        disabled={deletingId === item.id}
                                        style={{ display: "flex", alignItems: "center", gap: "5px" }}
                                    >
                                        {deletingId === item.id ? (
                                            <span className="inline-block w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
                                        ) : (
                                            "Remove"
                                        )}
                                    </button>
                                ) : (
                                    <button className="remove-btn" onClick={() => handleRemoveLocal(item.id)}>
                                        Remove
                                    </button>
                                )}
                            </div>
                        </div>
                    );
                })}

                <div className="daily-submit-section">
                    <div className="submit-btn-wrapper">
                        <button
                            className={`daily-save-btn ${!isSubmitEnabled ? "disabled-btn" : ""}`}
                            disabled={!isSubmitEnabled}
                            onClick={handleSubmit}
                        >
                            {submitLoading
                                ? <span className="inline-block w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
                                : "Submit"}
                        </button>
                    </div>

                    {/* ✅ PASTE HERE */}
                    {localNorms.length === 1 && totals && !isSubmitted && (
                        <div style={{ textAlign: "right", width: "100%" }}>
                            <p className="daily-warning">
                                Base norm added successfully. Now add daily activities to match the base norm.
                            </p>
                        </div>
                    )}
                    {!isSubmitEnabled && localNorms.length > 1 && totals && (
                        <div style={{ textAlign: "right", width: "100%" }}>
                            <p className="daily-warning">
                                {totals.totalOtherNorms < totals.baseNormValue
                                    ? `Need to add ${formatSecondsToHHMM(totals.baseNormValue - totals.totalOtherNorms)} more to match the base norm of ${formatSecondsToHHMM(
                                        totals.baseNormValue
                                    )}`
                                    // : `Total exceeds base norm by ${formatSecondsToHHMM(totals.totalOtherNorms - totals.baseNormValue)}`}
                                    : ''}
                            </p>
                        </div>
                    )}
                    {isSubmitted ? (
                        <div style={{ textAlign: "right", width: "100%" }}>
                            <p className="daily-success"></p>
                        </div>
                    ) : (
                        !isCalcValid && localNorms.length > 1 && totals && (
                            <div style={{ textAlign: "right", width: "100%" }}>
                                <p className="daily-warning">
                                    {totals.totalOtherNorms < totals.baseNormValue
                                        // ? `Need to Add ${formatSecondsToHHMM(totals.baseNormValue - totals.totalOtherNorms)} More Hours In Daily Activity To Match Base Norm Of ${formatSecondsToHHMM(
                                        //     totals.baseNormValue
                                        // )}`
                                        ? ''
                                        : `Daily activities are exceeding the base norm by ${formatSecondsToHHMM(
                                            totals.totalOtherNorms - totals.baseNormValue
                                        )}. Please match it to base norm.`}
                                </p>
                            </div>
                        )
                    )}
                </div>
            </div>

            {isModalOpen && (
                <div className="daily-modal-overlay">
                    <div className="daily-modal-content">
                        <div className="daily-modal-header">
                            <h4>{editingId ? "Edit Daily Norm" : "Daily Norm Configuration"}</h4>
                            <IoClose className="daily-close-icon" onClick={() => setIsModalOpen(false)} />
                        </div>
                        <div className="daily-modal-description">

                            <div className="daily-modal-fields">
                                <div className="field-group">
                                    <label className="daily-modal-label">
                                        Daily Activity <span className="text-[#AD0101]">*</span>
                                    </label>
                                    <input
                                        autoFocus
                                        type="text"
                                        placeholder="Enter Daily Activity"
                                        value={dailyActivity}
                                        onChange={(e) => setDailyActivity(e.target.value)}
                                        className={`daily-signin-inputs ${formSubmitted && !dailyActivity.trim() ? "input-error" : ""}`}
                                        style={{ width: "300px" }}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter') {
                                                e.preventDefault();
                                                handleSave();
                                            }
                                        }}
                                    />
                                    {formSubmitted && !dailyActivity.trim() && (
                                        <span className="daily-error-message">Please enter a daily activity.</span>
                                    )}
                                </div>
                                <div className="field-group">
                                    <label className="daily-modal-label">
                                        Daily Norms <span className="text-[#AD0101]">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        placeholder="hh:mm"
                                        value={dailyNorms}
                                        onChange={handleNormChange}
                                        className={`daily-signin-input ${formSubmitted && (!dailyNorms.trim() || (dailyNorms.trim().length !== 5 || !dailyNorms.match(/^(\d{2}):(\d{2})$/)) || !isValidTimeFormat(dailyNorms)) ? "input-error" : ""}`}
                                        style={{ width: "120px" }}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter') {
                                                e.preventDefault();
                                                handleSave();
                                            }
                                        }}
                                    />

                                    {formSubmitted && !dailyNorms.trim() && (
                                        <span className="daily-error-message">Please enter daily norms.</span>
                                    )}
                                    {formSubmitted && dailyNorms.trim() && (dailyNorms.length !== 5 || !dailyNorms.match(/^(\d{2}):(\d{2})$/)) && (
                                        <span className="daily-error-message">Please enter exactly four digits in hh:mm format (e.g., 09:21).</span>
                                    )}
                                    {formSubmitted && dailyNorms.trim() && dailyNorms.length === 5 && dailyNorms.match(/^(\d{2}):(\d{2})$/) && !isValidTimeFormat(dailyNorms) && (
                                        <span className="daily-error-message">Please enter a valid time in hh:mm format (e.g., 09:21, where hh is 00-23 and mm is 00-59).</span>
                                    )}
                                </div>
                            </div>

                        </div>
                        {!editingId && localNorms.length === 0 && (
                            <span style={{ color: "#4CAF50", fontWeight: "bold", marginBottom: "20px" }}>
                                You are adding the Base Norm.
                            </span>
                        )}
                        <div className="daily-modal-buttons">
                            <button className="daily-cancel-btn" onClick={() => setIsModalOpen(false)}>
                                Cancel
                            </button>
                            <button className="daily-save-btn" onClick={handleSave} disabled={saveLoading}>
                                {saveLoading ? <span className="inline-block w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></span> : "Save"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default DailyNormConfiguration;